import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getAllProducts } from '../../redux/reducers/productSlice';
import axios from '../../api/axios';
import CollectionCard from './CollectionCard';
import UtilityBar from './UtilityBar';
import FeaturedStrip from './FeaturedStrip';
import AssuranceRow from './AssuranceRow';

const CollectionsCards = () => {
  const dispatch = useDispatch();
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortOption, setSortOption] = useState('popularity');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  const { products, loading } = useSelector((state) => state.product);

  useEffect(() => {
    if (products.length === 0) {
      dispatch(getAllProducts());
    }
    fetchCategories();
  }, [dispatch, products.length]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/cms/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Build collections from database categories
  const collections = useMemo(() => {
    if (!categories || categories.length === 0) return [];

    return categories.map(cat => ({
      name: cat.name,
      value: cat.slug,
      description: cat.description || `Explore our ${cat.name.toLowerCase()} collection`,
      category: cat.slug,
      icon: '🌿',
      productCount: 0, // Will be populated when we fetch products by category
      imageUrl: cat.imageUrl
    }));
  }, [categories]);

  const categoryOptions = useMemo(() => {
    const cats = [{ label: 'All Collections', value: 'all' }];
    collections.forEach(col => {
      cats.push({
        label: col.name,
        value: col.category
      });
    });
    return cats;
  }, [collections]);

  const filteredCollections = useMemo(() => {
    let filtered = activeCategory === 'all' 
      ? collections 
      : collections.filter(c => c.category === activeCategory);

    // Apply sorting
    switch (sortOption) {
      case 'name-asc':
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return [...filtered].sort((a, b) => b.name.localeCompare(a.name));
      case 'newest':
        return [...filtered].reverse();
      default:
        return filtered;
    }
  }, [collections, activeCategory, sortOption]);

  if (loading || loadingCategories) {
    return (
      <div className="bg-[#f6f7f4] min-h-screen flex items-center justify-center">
        <div className="text-stone-600">Loading collections...</div>
      </div>
    );
  }

  return (
    <div className="bg-[#f6f7f4] min-h-screen">
      {/* Page Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-screen-xl mx-auto px-6 pt-8 pb-6">
          {/* Breadcrumb */}
          <nav className="text-xs text-stone-500 mb-3" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><a href="/" className="hover:text-stone-900">Home</a></li>
              <li>/</li>
              <li aria-current="page">Collections</li>
            </ol>
          </nav>

          {/* Title & Description */}
          <div className="space-y-2 max-w-2xl">
            <h1 className="text-3xl lg:text-4xl font-light text-stone-900 tracking-tight">
              Collections
            </h1>
            <p className="text-stone-600 leading-relaxed">
              Explore our curated range of Ayurvedic wellness products, thoughtfully formulated 
              to support your journey toward holistic health.
            </p>
          </div>
        </div>
      </header>

      {/* Utility Bar */}
      <UtilityBar
        resultCount={filteredCollections.length}
        categories={categoryOptions}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        sortOption={sortOption}
        onSortChange={setSortOption}
      />

      {/* Collections Grid */}
      <main className="max-w-screen-xl mx-auto px-6 py-12">
        {filteredCollections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {filteredCollections.map((collection) => (
              <CollectionCard key={collection.value} collection={collection} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-stone-600">
            No collections found
          </div>
        )}
      </main>

      {/* Featured Strip */}
      <FeaturedStrip />

      {/* Assurance Row */}
      <AssuranceRow />
    </div>
  );
};

export default CollectionsCards;
